import AppModal from '@components/AppModal'
import {
  ATTACHMENT_REDACTION_STATUS,
  normalizeRedactionStatus,
  REDACTION_STATUS_TAG,
} from '@consts/attachmentRedaction'
import { ApiService } from '@services/ApiService'
import appStore from '@stores/appStore'
import dashboardStore from '@stores/dashboard'
import { Alert, Button, Descriptions, notification, Spin, Tag } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useTrackedAction } from '../../activity/ActivitySubjectContext'

/**
 * What each status means for the person reading it, in the same words as the "How it
 * works" guide so the two never disagree. The point of every line is the same: whether
 * offshore staff can currently open this document.
 */
const STATUS_MEANING: Record<string, string> = {
  [ATTACHMENT_REDACTION_STATUS.Pending]:
    'Queued for automatic redaction. Nothing is visible to offshore staff yet.',
  [ATTACHMENT_REDACTION_STATUS.Processing]:
    'Being redacted now. A document stuck here for more than about 90 minutes is ' +
    'picked up automatically, marked Failed and raised for review — it will not sit ' +
    'here unnoticed.',
  [ATTACHMENT_REDACTION_STATUS.Redacted]:
    'Finished and released. Offshore staff can open the redacted copy; you still see both.',
  [ATTACHMENT_REDACTION_STATUS.NeedsReview]:
    'The system declined to release this and asked for a person. Hidden from offshore ' +
    'staff until someone decides — it is waiting in the Review Queue.',
  [ATTACHMENT_REDACTION_STATUS.Failed]:
    'Something went wrong and no redacted copy was produced. Hidden from offshore ' +
    'staff. Re-run it from the Attachment Redaction page once the cause is dealt with.',
  [ATTACHMENT_REDACTION_STATUS.Unsupported]:
    'This file is not a kind the pipeline can redact. Hidden from offshore staff — it ' +
    'needs the manual redaction tool, or a replacement file.',
}

/** Statuses where the reason line is an error rather than a note. */
const PROBLEM_STATUSES: string[] = [
  ATTACHMENT_REDACTION_STATUS.Failed,
  ATTACHMENT_REDACTION_STATUS.Unsupported,
  ATTACHMENT_REDACTION_STATUS.NeedsReview,
]

interface Props {
  attachmentId: string
  /** Status already known from the list, so the modal says something before the fetch lands. */
  fallbackStatus?: unknown
  onClose: () => void
}

/**
 * Why one document is in the state it is in.
 *
 * The list carries the status but not the reason — reasons are only loaded per attachment,
 * which is right: they are long, they are only wanted when something looks wrong, and on a
 * list of fifty they would be fifty strings nobody reads. So this fetches on open.
 */
const StatusDetailModal = ({
  attachmentId,
  fallbackStatus,
  onClose,
}: Props) => {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const trackAction = useTrackedAction()
  const [detail, setDetail] = useState<any>(null)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')

    try {
      const res = await ApiService.Attachment.GetById(attachmentId)
      setDetail(res.data)
    } catch (e: any) {
      setError(
        e?.response?.data?.error?.message ??
          'The redaction details for this document could not be loaded.'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentId])

  const redaction = detail?.attachmentRedaction
  const status = normalizeRedactionStatus(
    detail?.redactionStatus ?? fallbackStatus
  )
  const tag = REDACTION_STATUS_TAG[status]
  const isProblem = PROBLEM_STATUSES.includes(status)
  const needsReview = status === ATTACHMENT_REDACTION_STATUS.NeedsReview

  // Mirrors the server's own guard (feature + roles + not offshore) rather than
  // approximating it. This decides what to OFFER; the API enforces it regardless.
  const canManage =
    appStore.features?.AttachmentRedaction &&
    (appStore.isTenantAdmin || appStore.isAdminStaff) &&
    !appStore.ifOffshoreStaff()

  const canOpenRedactionPage = canManage
  const hasRedactedCopy = !!redaction?.redactedFileId
  const documentName = detail?.name || detail?.fileName || 'document'

  const previewRedacted = () =>
    appStore.openPDFViewer(
      redaction.redactedFileId,
      `${documentName} (Redacted)`
    )

  /** Runs an action, then re-reads the record so the modal shows what it did. */
  const run = async (action: () => Promise<any>, success: string) => {
    setBusy(true)
    try {
      await action()
      notification.success({ message: 'Success', description: success })
      await load()
    } catch (e: any) {
      notification.error({
        message: 'Error',
        description:
          e?.response?.data?.error?.message ??
          'The action could not be completed.',
      })
    } finally {
      setBusy(false)
    }
  }

  // The confirm modal holds the action open: the request in onOk joins it.
  const deleteRedactedCopy = () =>
    trackAction('Document.DeleteRedactedCopy', () =>
      appStore.openConfirmModal({
        message:
          'Delete the redacted copy? The document is hidden from offshore staff until it ' +
          'is redacted again.',
        onOk: () =>
          run(
            () =>
              ApiService.AttachmentRedaction.DeleteRedactedCopy(attachmentId),
            'Redacted copy deleted — the document is hidden from offshore staff.'
          ),
      })
    )

  const redactAgain = () =>
    trackAction('Document.RedactAgain', () =>
      appStore.openConfirmModal({
        // Says what happens to offshore access, because that is the part with a
        // consequence — the reprocessing itself is unremarkable.
        message: hasRedactedCopy
          ? 'Redact this document again from the original? The current copy is withdrawn ' +
            'immediately, so offshore staff lose access until the new one is published.'
          : 'Redact this document again from the original?',
        onOk: () =>
          run(
            () => ApiService.AttachmentRedaction.RedactAgain(attachmentId),
            'Queued — the document will be redacted again from the original.'
          ),
      })
    )

  /**
   * Lands on the tab that can actually do something about this status: the Review Queue
   * when a person has been asked for, otherwise Documents, where Re-run and the manual
   * tool live. The document is carried across so the target does not open on a list of
   * everything with no idea which row was meant.
   */
  const goToRedactionPage = () => {
    const params = new URLSearchParams({
      tab: needsReview ? 'review' : 'documents',
      attachmentId,
    })

    const name = detail?.name || detail?.fileName
    if (name) {
      params.append('document', name)
    }

    onClose()
    dashboardStore.open(`/view/attachment-redaction?${params.toString()}`)
  }

  return (
    <AppModal
      open
      title="Redaction status"
      width={640}
      onCancel={onClose}
      footer={null}
    >
      <Spin spinning={loading}>
        <div className="min-h-[160px]">
          {error && (
            <Alert type="error" showIcon message={error} className="mb-3" />
          )}

          <div className="flex items-center gap-2 mb-3">
            {tag && <Tag color={tag.color}>{tag.label}</Tag>}
            <span className="text-sm text-gray-600">
              {STATUS_MEANING[status] ??
                'This document has not entered the redaction pipeline.'}
            </span>
          </div>

          {/* The reason is the whole point of opening this. Shown as an alert when it
              explains a failure, and as a plain note when it qualifies a success —
              "published in Highlight mode" is not an error but does need reading. */}
          {redaction?.statusReason && (
            <Alert
              className="mb-3"
              type={isProblem ? 'error' : 'info'}
              showIcon
              message={isProblem ? 'Reason' : 'Note'}
              description={redaction.statusReason}
            />
          )}

          {!loading && !redaction?.statusReason && isProblem && (
            <Alert
              className="mb-3"
              type="warning"
              showIcon
              message="No reason was recorded for this failure."
              description="Check the Attachment Redaction page and the API log for this document."
            />
          )}

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Document">
              {detail?.name || detail?.fileName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Last processed">
              {redaction?.processedTime
                ? dayjs(redaction.processedTime).format('DD MMM YYYY - HH:mm')
                : 'Not processed yet'}
            </Descriptions.Item>
            <Descriptions.Item label="Attempts">
              {(redaction?.retryCount ?? 0) + 1}
            </Descriptions.Item>
            <Descriptions.Item label="Redacted copy">
              {redaction?.redactedFileId ? 'Published' : 'None'}
            </Descriptions.Item>
          </Descriptions>

          <div className="mt-4 flex justify-end gap-2 flex-wrap">
            {/* Pending and Processing change on their own; a reader watching one should
                not have to close and reopen to find out. */}
            <Button onClick={load} loading={loading}>
              Refresh
            </Button>

            {/* The actions only make sense against a copy that exists, and only for
                someone allowed to manage redaction. The server enforces the same rule —
                this decides what to OFFER, not what is permitted. */}
            {canManage && hasRedactedCopy && (
              <>
                <Button onClick={previewRedacted}>Preview redacted file</Button>
                <Button danger onClick={deleteRedactedCopy} loading={busy}>
                  Delete redacted file
                </Button>
              </>
            )}

            {canManage && (
              <Button onClick={redactAgain} loading={busy}>
                Redact again
              </Button>
            )}

            {/* Knowing why a document is held is only half of it — the next step is on
                another page, and making someone find it themselves is where a status
                screen stops being useful. Hidden for anyone who cannot open that page:
                a link that bounces is worse than no link. */}
            {canOpenRedactionPage && (
              <Button type="primary" onClick={goToRedactionPage}>
                {needsReview
                  ? 'Open in Review Queue'
                  : 'Open in Attachment Redaction'}
              </Button>
            )}
          </div>
        </div>
      </Spin>
    </AppModal>
  )
}

export default StatusDetailModal
